class HeroScroll {
    constructor() {
        this.heroImageContainer = document.getElementById('hero-image-container');
        this.heroImage = document.getElementById('hero-image');
        this.galleryContainer = document.querySelector('.gallery-scroll-container');
        this.galleryWrapper = document.querySelector('.gallery-sticky-wrapper');
        this.slides = document.querySelectorAll('.gallery-slide');

        if (!this.heroImageContainer || !this.galleryContainer || this.slides.length === 0) {
            return;
        }

        this.currentSlide = 0;
        this.totalSlides = this.slides.length;
        this.originalImageRect = null;
        this.zoomStart = 0;
        this.zoomEnd = 0;
        this.fadeDistance = 0;
        this.galleryStart = 0;
        this.pinMetrics = null;
        this.ticking = false;
        this.isMobile = window.innerWidth <= 1023;

        this.init();
    }

    init() {
        this.updateContainerHeight();
        window.addEventListener('load', () => {
            this.captureImagePosition();
            this.calculatePoints();
            this.update();
        });

        window.addEventListener('scroll', () => this.onScroll(), { passive: true });
        window.addEventListener('resize', () => {
            this.isMobile = window.innerWidth <= 1023;
            this.captureImagePosition();
            this.calculatePoints();
            this.updateContainerHeight();
            this.update();
        });
    }

    captureImagePosition() {
        const rect = this.heroImageContainer.getBoundingClientRect();
        this.originalImageRect = {
            top: rect.top + window.pageYOffset,
            left: rect.left,
            width: rect.width,
            height: rect.height,
        };
    }

    calculatePoints() {
        if (!this.originalImageRect) {
            return;
        }

        const imageCenterYDoc = this.originalImageRect.top + (this.originalImageRect.height / 2);
        this.zoomStart = imageCenterYDoc - (window.innerHeight / 2);
        this.zoomEnd = this.galleryContainer.offsetTop;
        if (this.zoomEnd <= this.zoomStart) {
            this.zoomEnd = this.zoomStart + window.innerHeight;
        }

        this.fadeDistance = Math.max(120, window.innerHeight * 0.25);
        this.galleryStart = this.zoomEnd + this.fadeDistance;
    }

    updateContainerHeight() {
        const viewportHeight = window.innerHeight;
        const scrollHeightPerSlide = viewportHeight * 0.5;
        const totalHeight = viewportHeight + (scrollHeightPerSlide * this.totalSlides);
        this.galleryContainer.style.minHeight = `${totalHeight}px`;
    }

    onScroll() {
        if (this.ticking) {
            return;
        }

        this.ticking = true;
        window.requestAnimationFrame(() => {
            this.ticking = false;
            this.update();
        });
    }

    update() {
        if (!this.originalImageRect) {
            this.captureImagePosition();
            this.calculatePoints();
        }

        const scrollY = window.pageYOffset;

        if (scrollY < this.zoomStart) {
            this.resetImagePosition();
            return;
        }

        if (scrollY >= this.zoomStart && scrollY < this.zoomEnd) {
            this.handleImageZoom(scrollY);
            return;
        }

        if (scrollY >= this.zoomEnd && scrollY < this.galleryStart) {
            this.handleFadeAtFinal(scrollY);
            return;
        }

        if (scrollY >= this.galleryStart) {
            this.handleGalleryScroll(scrollY, this.galleryStart);
        }
    }

    resetImagePosition() {
        this.heroImageContainer.classList.remove('floating');
        this.galleryWrapper.classList.remove('visible');
        this.pinMetrics = null;
        this.galleryWrapper.style.opacity = '';
        this.galleryWrapper.style.pointerEvents = '';
        this.heroImage.style.position = '';
        this.heroImage.style.top = '';
        this.heroImage.style.left = '1rem';
        this.heroImage.style.right = '';
        this.heroImage.style.width = '';
        this.heroImage.style.height = '';
        this.heroImage.style.maxWidth = '';
        this.heroImage.style.transform = '';
        this.heroImage.style.opacity = '1';
        this.heroImage.style.zIndex = '';
        this.heroImage.style.display = '';
        this.heroImage.classList.remove('hero-image-mobile');
    }

    easeOutCubic(t) {
        return 1 - Math.pow(1 - t, 3);
    }

    easeInOutCubic(t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }

    handleImageZoom(scrollY) {
        if (!this.pinMetrics) {
            const rect = this.heroImageContainer.getBoundingClientRect();
            this.pinMetrics = {
                startRight: window.innerWidth - (rect.left + rect.width),
                startWidth: rect.width || 309,
                startHeight: rect.height || 254,
            };
        }

        const rawProgress = (scrollY - this.zoomStart) / (this.zoomEnd - this.zoomStart);
        const clamped = Math.min(Math.max(rawProgress, 0), 1);
        const p = this.easeOutCubic(clamped);

        const startWidth = this.pinMetrics.startWidth;
        const startHeight = this.pinMetrics.startHeight;
        const endWidth = Math.min(1080, window.innerWidth - 30);
        const endHeight = (endWidth / 5.5) * 4;

        const currentWidth = startWidth + (endWidth - startWidth) * p;
        const currentHeight = startHeight + (endHeight - startHeight) * p;

        const endRight = (window.innerWidth - endWidth) / 2;
        const currentRight = this.pinMetrics.startRight + (endRight - this.pinMetrics.startRight) * p;

        this.heroImage.style.position = 'fixed';
        this.heroImage.style.top = '50%';
        this.heroImage.style.transform = 'translateY(-50%)';
        this.heroImage.style.zIndex = '50';
        this.heroImage.style.display = 'block';
        this.heroImage.style.opacity = '1';
        this.galleryWrapper.style.opacity = '0';
        this.galleryWrapper.style.pointerEvents = 'none';
        this.galleryWrapper.classList.remove('visible');

        if (this.isMobile) {
            // Mobile: use CSS class instead of inline styles
            this.heroImage.classList.add('hero-image-mobile');
            this.heroImage.style.left = '1rem';
            this.heroImage.style.right = '1rem';
            this.heroImage.style.width = '';
            this.heroImage.style.height = '';
            this.heroImage.style.maxWidth = '';
        } else {
            // Desktop: set width and height via JS, use calculated right position
            this.heroImage.classList.remove('hero-image-mobile');
            this.heroImage.style.left = 'auto';
            this.heroImage.style.right = `${currentRight}px`;
            this.heroImage.style.width = `${currentWidth}px`;
            this.heroImage.style.height = `${currentHeight}px`;
        }
    }

    handleFadeAtFinal(scrollY) {
        if (!this.pinMetrics) {
            const rect = this.heroImageContainer.getBoundingClientRect();
            this.pinMetrics = {
                startRight: window.innerWidth - (rect.left + rect.width),
                startWidth: rect.width || 309,
                startHeight: rect.height || 254,
            };
        }

        const endWidth = Math.min(1080, window.innerWidth - 30);
        const endHeight = (endWidth / 5.5) * 4;
        const endRight = (window.innerWidth - endWidth) / 2;

        this.heroImage.style.position = 'fixed';
        this.heroImage.style.top = '50%';
        this.heroImage.style.transform = 'translateY(-50%)';
        this.heroImage.style.zIndex = '50';

        if (this.isMobile) {
            // Mobile: use CSS class instead of inline styles
            this.heroImage.classList.add('hero-image-mobile');
            this.heroImage.style.left = '1rem';
            this.heroImage.style.right = '1rem';
            this.heroImage.style.width = '';
            this.heroImage.style.height = '';
            this.heroImage.style.maxWidth = '';
        } else {
            // Desktop: set width and height via JS, use calculated right position
            this.heroImage.classList.remove('hero-image-mobile');
            this.heroImage.style.left = 'auto';
            this.heroImage.style.right = `${endRight}px`;
            this.heroImage.style.width = `${endWidth}px`;
            this.heroImage.style.height = `${endHeight}px`;
        }

        const rawT = (scrollY - this.zoomEnd) / (this.galleryStart - this.zoomEnd);
        const t = Math.min(Math.max(rawT, 0), 1);
        const fadeT = this.easeInOutCubic(t);

        this.heroImage.style.opacity = String(1 - fadeT);
        this.heroImage.style.display = 'block';
        this.galleryWrapper.style.opacity = String(fadeT);
        this.galleryWrapper.style.pointerEvents = fadeT > 0.01 ? 'auto' : 'none';
        this.galleryWrapper.classList.toggle('visible', fadeT > 0.01);
    }

    handleGalleryScroll(scrollY, galleryStart) {
        this.heroImage.style.opacity = '0';
        this.heroImage.style.display = 'none';
        if (!this.galleryWrapper.style.opacity) {
            this.galleryWrapper.style.opacity = '1';
            this.galleryWrapper.style.display = 'flex';
        }
        this.galleryWrapper.style.pointerEvents = 'auto';

        const containerHeight = this.galleryContainer.offsetHeight;
        const viewportHeight = window.innerHeight;
        const scrollEnd = this.galleryContainer.offsetTop + containerHeight - viewportHeight;

        if (scrollY > scrollEnd) {
            return;
        }

        const scrollProgress = scrollY - galleryStart;
        const scrollRange = scrollEnd - galleryStart;
        const progressPercent = scrollProgress / scrollRange;

        const slideIndex = Math.floor(progressPercent * this.totalSlides);
        const newSlide = Math.min(Math.max(slideIndex, 0), this.totalSlides - 1);

        if (newSlide !== this.currentSlide) {
            this.slides[this.currentSlide].classList.remove('active');
            this.slides[newSlide].classList.add('active');
            this.currentSlide = newSlide;
        }
    }
}

let heroScrollInstance = null;

export function initHeroScroll() {
    heroScrollInstance = new HeroScroll();
}
